import Table from "@mui/material/Table/Table";
import TableBody from "@mui/material/TableBody/TableBody";
import TableCell from "@mui/material/TableCell/TableCell";
import TableContainer from "@mui/material/TableContainer/TableContainer";
import TableHead from "@mui/material/TableHead/TableHead";
import TableRow from "@mui/material/TableRow/TableRow";
import { LeadTableSummary, SummaryType, SummaryTypeText } from "../shared/Constants";
import Button from "@mui/material/Button/Button";
import FormControl from "@mui/material/FormControl/FormControl";
import Select from "@mui/material/Select/Select";
import MenuItem from "@mui/material/MenuItem/MenuItem";
import Chip from "@mui/material/Chip/Chip";
import { HelpersService } from "../../../../services/Helpers";
import { LeadViewModel, UserShortResponse, Category } from "../../../../api";

const headCells = [
  { id: "leadName", label: "Lead name" },
  { id: "category", label: "Category" },
  { id: "termination", label: "Termination" },
  { id: "summaryType", label: "Summary type" },
];

interface ShareLeadsTableProps {
  leads: LeadTableSummary[];
  onPreviewSelect: Function;
  onPreviewTypeChange: Function;
  attorneyIds?: number[];
  attorneys?: UserShortResponse[];
  filterEligibleOnly?: boolean;
}

export default function ShareLeadsTable(props: ShareLeadsTableProps) {
  const maxMonths = HelpersService.getRequiredTerminationAgeMonths(
    props.attorneyIds ?? [],
    props.attorneys ?? []
  );

  const isEligible = (lead: LeadViewModel) => {
    if (!maxMonths) return true;
    if (lead.category !== Category.EMPLOYMENT && lead.category !== Category.CLASS_ACTION) return true;
    return HelpersService.isLeadWithinTerminationAge(lead, maxMonths);
  };

  const rows = (props.filterEligibleOnly
    ? props.leads.filter((row) => isEligible(row.lead))
    : props.leads
  ).map((row, index) => ({ row, index: props.leads.indexOf(row) }));

  const renderRow = (row: LeadTableSummary, index: number) => {
    const eligible = isEligible(row.lead);
    const termDate = row.lead.employmentDetails?.terminationDate;

    return (
      <TableRow hover tabIndex={-1} key={row.lead.id}>
        <TableCell align="left">
          {row.lead.firstName} {row.lead.lastName}
        </TableCell>
        <TableCell align="left">
          {HelpersService.camelToString(row.lead.category)}
        </TableCell>
        <TableCell align="left">
          {termDate ?? "—"}
          {maxMonths && (
            <Chip
              size="small"
              className="ms-2"
              color={eligible ? "success" : "warning"}
              label={eligible ? "Eligible" : `>${maxMonths} mo`}
            />
          )}
        </TableCell>
        <TableCell align="left">
          <FormControl required variant="outlined" sx={{ m: 1, minWidth: 120 }}>
            <Select
              value={row.summaryType}
              onChange={(e) => props.onPreviewTypeChange(index, e.target.value)}
            >
              {Object.keys(SummaryType).map((key) => (
                <MenuItem key={key} value={key}>
                  {SummaryTypeText[key]}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </TableCell>
        <TableCell align="left">
          <Button
            variant="text"
            color="primary"
            size="small"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              props.onPreviewSelect(index);
            }}
          >
            Preview
          </Button>
        </TableCell>
      </TableRow>
    );
  };

  return (
    <TableContainer>
      <Table sx={{ minWidth: 600 }} aria-labelledby="tableTitle" size="medium">
        <TableHead>
          <TableRow>
            {headCells.map((headCell) => (
              <TableCell key={headCell.id}>{headCell.label}</TableCell>
            ))}
            <TableCell align="left" className="ps-5">
              Actions
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map(({ row, index }) => renderRow(row, index))}
          {rows.length === 0 && (
            <TableRow>
              <TableCell colSpan={6} align="center">
                No leads match the termination date filter for selected attorneys.
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
